import { FC, useRef, useMemo, useState, useEffect, ReactNode } from 'react'
import {
  Paper,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  TableContainer,
  Table,
  styled,
  TableBody,
} from '@mui/material'
import EditIcon from '@/assets/icons/actions/edit.svg'
import ViewIcon from '@/assets/icons/actions/view.svg'
import { useVirtualizedTable } from '@/hooks/shared/useVirtualizedTable'
import {
  TableWrapper,
  TableContainerStyled,
  VirtualTableContainer,
  VirtualRow,
  VirtualCell,
} from '@/components/table/StyledTableComponents'
import { TableHeader } from '@/components/table/TableHeader'
import { TableFooter } from '@/components/table/TableFooter'
import { NoResultsAlert } from '@/components/notification/NoResultsAlert'
import { AssignProvinceModal } from '@/components/modal/area-management/areas/AssignProvinceModal'
import ViewAreaModal from '@/components/modal/area-management/areas/ViewAreaModal'
import type { SortableTableProps } from '@/utilities/types/table-props'
import type { TableColumn, AreaData, BranchData } from '@/utilities/types/table'
import DeleteIcon from '@mui/icons-material/Delete'
import DateFormatter from '@/utilities/date-formatter'
import { FilterState } from '@/utilities/types/shared-filter'
import { ProvinceChipsDisplay } from '@/components/modal/area-management/areas/ViewProvinceCollumnChips'
import { assignProvince } from '@/api'
import { AxiosError } from 'axios'
import { useAreasVisualizedTable } from '@/hooks/shared/useAreasVisualizedTable'
import { AreasTableFooter } from '../../AreasTableFooter'
import dayjs from 'dayjs'
import { c } from 'node_modules/vite/dist/node/types.d-aGj9QkWt'

export const StyledRow = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  height: '52px',
  padding: '16px',
  '&:nth-of-type(even)': {
    backgroundColor: '#f5f5f5',
  },
  '&:nth-of-type(odd)': {
    backgroundColor: 'white',
  },
}))

export const StyledCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  wordBreak: 'break-word',
  textAlign: 'center',
  fontSize: '12px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}))

const columns: TableColumn[] = [
  { id: 'id', label: 'ID', width: '10%', sortable: true },
  { id: 'name', label: 'Area Name', width: '15%', sortable: true },
  { id: 'code', label: 'Area Code', width: '10%', sortable: true },
  { id: 'branches', label: 'Branches', width: '25%', sortable: false },
  { id: 'dateCreated', label: 'Created Date', width: '15%', sortable: true },
  { id: 'dateModified', label: 'Modified Date', width: '15%', sortable: true },
  { id: 'action', label: 'Action', width: '10%', align: 'center' },
]

interface ExtendedAreaTableProps extends SortableTableProps<AreaData> {
  branches: BranchData[]
  onAreaAssigned?: () => Promise<void>
  refreshTable: () => Promise<void>
}

// const customAreaFilter = (data: AreaData[], filterState: FilterState) => {
//   return data.filter((item) => {
//     return Object.entries(filterState.regularFilters).every(
//       ([field, selectedValues]) => {
//         if (!selectedValues || selectedValues.length === 0) return true

//         if (field === 'name' && item.provinceList) {
//           return selectedValues.some((selectedProvince) =>
//             item.provinceList.some(
//               (province) => province.name === selectedProvince,
//             ),
//           )
//         }

//         const itemValue = item[field as keyof AreaData]
//         if (Array.isArray(selectedValues)) {
//           return selectedValues.includes(itemValue as string)
//         }
//         return itemValue === selectedValues
//       },
//     )
//   })
// }

export const AreaManagementAreaTable: FC<ExtendedAreaTableProps> = ({
  data = [],
  filterState,
  isLoading,
  error,
  branches = [],
  onSort,
  onAreaAssigned,
  refreshTable,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalElem,
  handleSort,
  handleDeleteArea,
}) => {
  const parentRef = useRef<HTMLDivElement>(null)
  const [showNoResults, setShowNoResults] = useState(false)
  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [selectedArea, setSelectedArea] = useState<AreaData | null>(null)
  const [assignModalOpen, setAssignModalOpen] = useState(false)
  const [isAssigning, setIsAssigning] = useState(false)
  const [assignError, setAssignError] = useState<string | null>(null)
  const [assignFormData, setAssignFormData] = useState<BranchData[]>([])
  console.log('checkthisjay', selectedArea)
  const {
    virtualRows,
    totalSize,
    paginatedItems,
    handlePageChange,
    handleRowsPerPageChange,
    handleVirtualizedScroll,
  } = useAreasVisualizedTable({
    data,
    filterState,
    containerRef: parentRef,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalElem,
  })

  useEffect(() => {
    if (data.length <= 0 && isLoading) {
      setShowNoResults(true)
    }
  }, [data.length])

  const handleViewModalOpen = (area: AreaData) => {
    setSelectedArea(area)
    setViewModalOpen(true)
  }

  const handleAssignModalOpen = (area: AreaData) => {
    setSelectedArea(area)
    console.log('selectedArea', area.branches)
    setAssignFormData(area.branches || [])
    setAssignModalOpen(true)
  }
  // include the updated name in the handler parameters
  const handleAssignSubmit = async (formData: BranchData[], name: string) => {
    if (!selectedArea) return;

    setIsAssigning(true);
    setAssignError(null);

    try {
      await assignProvince({
        areaID: selectedArea.id,
        name, // ✅ send the editable name to the backend
        branchIDs: formData.map((item) => item.id),
      });

      if (onAreaAssigned) {
        await onAreaAssigned();
      }

      setAssignModalOpen(false);
      setSelectedArea(null);
      setAssignFormData([]);

      await refreshTable();
    } catch (error) {
      if (error instanceof AxiosError) {
        setAssignError(
          error.response
            ? error.response.data.message
            : 'Failed to assign branch',
        );
      } else {
        setAssignError('An unexpected error occurred.');
      }
    } finally {
      setIsAssigning(false);
    }
  };


  if (isLoading) {
    return (
      <Box
        sx={{
          height: '70vh',
          width: '100%',
          background: 'white',
          zIndex: 1,
          display: 'fleex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
          marginTop: '2px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        Error: {error instanceof Error ? error.message : 'Unknown error'}
      </Box>
    )
  }

  return (
    <div
      style={{
        boxShadow: 'none',
        width: '100%',
        zIndex: 1,
        marginTop: '2px',
      }}
    >
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 650, minHeight: '600px' }}
          aria-label="simple table"
        >
          <TableHeader
            columns={columns}
            filterState={filterState}
            onSort={onSort}
            handleSort={handleSort}
          />
          <TableBody
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {paginatedItems?.map((row, index) => (
              <StyledRow key={index}>
                {columns.map((column) => {
                  if (column.id === 'id') {
                    return (
                      <StyledCell style={{ width: column.width }}>
                        {row.id}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'name') {
                    return (
                      <StyledCell width={column.width}>{row.name}</StyledCell>
                    )
                  }

                  if (column.id === 'code') {
                    return (
                      <StyledCell width={column.width}>{row.code}</StyledCell>
                    )
                  }

                  if (column.id === 'branches') {
                    if (Array.isArray(row.branches)) {
                      return (
                        <StyledCell width={column.width}>
                          <ProvinceChipsDisplay branches={row.branches} />
                        </StyledCell>
                      )
                    } else {
                      return <StyledCell>'-'</StyledCell>
                    }
                  }
                  if (column.id === 'dateCreated') {
                    return (
                      <StyledCell width={column.width}>
                        {dayjs(row.dateCreated).format('MMM DD, YYYY')}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'dateModified') {
                    return (
                      <StyledCell width={column.width}>
                        {dayjs(row.dateModified).format('MMM DD, YYYY')}
                      </StyledCell>
                    )
                  }
                  if (column.id === 'action') {
                    return (
                      <StyledCell width={column.width}>
                        <Stack
                          direction="row"
                          spacing={1}
                          justifyContent="center"
                        >
                          <IconButton
                            onClick={() => handleViewModalOpen(row)}
                            sx={{
                              padding: '4px',
                              '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                            }}
                          >
                            <img
                              src={ViewIcon}
                              alt="View"
                              width={16}
                              height={16}
                            />
                          </IconButton>
                          <IconButton
                            onClick={() => handleAssignModalOpen(row)}
                            sx={{
                              padding: '4px',
                              '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                            }}
                          >
                            <img
                              src={EditIcon}
                              alt="Edit"
                              width={16}
                              height={16}
                            />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteArea(row.id)}
                            sx={{
                              padding: '4px',
                              '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                            }}
                          >
                            <DeleteIcon
                              sx={{
                                fontSize: 20,
                                color: 'inherit',
                              }}
                            />
                          </IconButton>
                        </Stack>
                      </StyledCell>
                    )
                  }
                })}
              </StyledRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <ViewAreaModal
        open={viewModalOpen}
        onClose={() => {
          setViewModalOpen(false)
          setSelectedArea(null)
        }}
        data={selectedArea}
      />

      {selectedArea && (
        <AssignProvinceModal
          open={assignModalOpen}
          onClose={() => {
            setAssignModalOpen(false)
            setSelectedArea(null)
            setAssignError(null)
          }}
          onSubmit={handleAssignSubmit}
          formData={assignFormData}
          setFormData={setAssignFormData}
          branches={branches}
          existingBranches={selectedArea.branches || []}
          areaName={selectedArea.name}
          areaCode={selectedArea.code}
          isLoading={isAssigning}
          error={assignError}
        />
      )}
      <AreasTableFooter
        rowsPerPage={rowsPerPage}
        totalCount={totalElem}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
    </div>
  )
}
